/**
 * Player Stats Storage
 * Stores match history and aggregates in localStorage, keyed by wallet address
 */

export interface MatchHistoryEntry {
  matchId: string;
  timestamp: number;
  rank: number;
  totalPlayers: number;
  score: number;
  payout: number; // In cents (USDC * 100)
  entryFee: number; // In cents
}

export interface PlayerStats {
  walletAddress: string;
  totalPayouts: number; // In cents
  totalEntryFees: number; // In cents
  unclaimedPayouts: number; // In cents - payouts won but not yet claimed
  gamesPlayed: number;
  wins: number; // 1st place finishes
  matchHistory: MatchHistoryEntry[];
}

const STORAGE_KEY_PREFIX = 'mine-or-die-stats-';
const MAX_HISTORY_ENTRIES = 50; // Keep last 50 matches

/**
 * Get storage key for a wallet address
 */
function getStorageKey(walletAddress: string): string {
  return `${STORAGE_KEY_PREFIX}${walletAddress.toLowerCase()}`;
}

/**
 * Load player stats from localStorage
 */
export function loadPlayerStats(walletAddress: string): PlayerStats {
  const key = getStorageKey(walletAddress);
  const stored = localStorage.getItem(key);

  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.error('Failed to parse player stats:', e);
    }
  }

  // Return default stats
  return {
    walletAddress: walletAddress.toLowerCase(),
    totalPayouts: 0,
    totalEntryFees: 0,
    unclaimedPayouts: 0,
    gamesPlayed: 0,
    wins: 0,
    matchHistory: [],
  };
}

/**
 * Save player stats to localStorage
 */
export function savePlayerStats(stats: PlayerStats): void {
  const key = getStorageKey(stats.walletAddress);
  localStorage.setItem(key, JSON.stringify(stats));
}

/**
 * Record a match result
 */
export function recordMatchResult(
  walletAddress: string,
  matchId: string,
  rank: number,
  totalPlayers: number,
  score: number,
  payoutCents: number,
  entryFeeCents: number = 100 // Default $1
): PlayerStats {
  const stats = loadPlayerStats(walletAddress);

  // Create history entry
  const entry: MatchHistoryEntry = {
    matchId,
    timestamp: Date.now(),
    rank,
    totalPlayers,
    score,
    payout: payoutCents,
    entryFee: entryFeeCents,
  };

  // Add to history (newest first)
  stats.matchHistory.unshift(entry);

  // Trim history to max entries
  if (stats.matchHistory.length > MAX_HISTORY_ENTRIES) {
    stats.matchHistory = stats.matchHistory.slice(0, MAX_HISTORY_ENTRIES);
  }

  // Update aggregates
  stats.gamesPlayed++;
  stats.totalPayouts += payoutCents;
  stats.totalEntryFees += entryFeeCents;

  if (rank === 1) {
    stats.wins++;
  }

  // Save
  savePlayerStats(stats);

  return stats;
}

/**
 * Get recent match history (last N matches)
 */
export function getRecentMatches(walletAddress: string, count: number = 10): MatchHistoryEntry[] {
  const stats = loadPlayerStats(walletAddress);
  return stats.matchHistory.slice(0, count);
}

/**
 * Calculate net profit/loss
 */
export function getNetProfit(stats: PlayerStats): number {
  return stats.totalPayouts - stats.totalEntryFees;
}

/**
 * Format cents as dollars
 */
export function formatCentsAsDollars(cents: number): string {
  const dollars = cents / 100;
  const sign = dollars >= 0 ? '' : '-';
  return `${sign}$${Math.abs(dollars).toFixed(2)}`;
}

/**
 * Format timestamp as relative time
 */
export function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  // Format as date
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Add unclaimed payout (when player wins but hasn't claimed yet)
 */
export function addUnclaimedPayout(walletAddress: string, amountCents: number): void {
  const stats = loadPlayerStats(walletAddress);
  stats.unclaimedPayouts += amountCents;
  savePlayerStats(stats);
}

/**
 * Claim payout (move from unclaimed to claimed)
 */
export function claimPayout(walletAddress: string, amountCents: number): void {
  const stats = loadPlayerStats(walletAddress);
  stats.unclaimedPayouts = Math.max(0, stats.unclaimedPayouts - amountCents);
  savePlayerStats(stats);
}

/**
 * Clear all stats for a wallet (for testing)
 */
export function clearPlayerStats(walletAddress: string): void {
  const key = getStorageKey(walletAddress);
  localStorage.removeItem(key);
}
